import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, Video, Link as LinkIcon, File, FolderOpen } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const response = await axios.get(`${API}/resources`);
        setResources(response.data || []);
      } catch (error) {
        console.error("Error fetching resources:", error);
        setResources([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "pdf":
        return <FileText className="w-8 h-8 text-red-500" />;
      case "video":
        return <Video className="w-8 h-8 text-blue-500" />;
      case "link":
        return <LinkIcon className="w-8 h-8 text-teal-500" />;
      default:
        return <File className="w-8 h-8 text-slate-500" />;
    }
  };

  const handleDownload = async (resource) => {
    // Track download
    try {
      await axios.get(`${API}/resources/${resource.id}`);
    } catch (error) {
      console.error("Error tracking download:", error);
    }
    
    // Open resource URL
    window.open(resource.file_url, "_blank");
  };

  const categories = ["All", ...new Set(resources.map(r => r.category))];
  
  const filteredResources = activeCategory === "All" 
    ? resources 
    : resources.filter(r => r.category === activeCategory);

  return (
    <div className="pt-20" data-testid="resources-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              Resource Library
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              Helpful <span className="text-amber-700 italic">Resources</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              Access educational materials, fact sheets, guides, and other resources to learn more about sickle cell disease and available support.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      {resources.length > 0 && (
        <section className="py-8 bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === category
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`filter-${category.toLowerCase()}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Resources Grid */}
      <section className="py-16 bg-slate-50" data-testid="resources-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : resources.length === 0 ? (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-12 text-center">
                <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-6" />
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-3">
                  Resources Coming Soon
                </h3>
                <p className="text-slate-500 mb-6">
                  We're building a comprehensive resource library with educational materials, fact sheets, and guides about sickle cell disease. Check back soon!
                </p>
                <Link to="/contact">
                  <Button className="bg-teal-700 hover:bg-teal-800 text-white">
                    Request Information
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource) => (
                <Card key={resource.id} className="card-hover" data-testid={`resource-${resource.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-slate-100 rounded-lg">
                        {getFileIcon(resource.file_type)}
                      </div>
                      <div className="flex-1">
                        <span className="inline-block bg-amber-100 text-amber-800 text-xs font-medium px-2 py-1 rounded mb-2">
                          {resource.category}
                        </span>
                        <h3 className="font-serif text-lg font-semibold text-slate-900 mb-2">
                          {resource.title}
                        </h3>
                        <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                          {resource.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">
                            {resource.download_count} downloads
                          </span>
                          <Button
                            onClick={() => handleDownload(resource)}
                            variant="outline"
                            size="sm"
                            className="text-teal-700 border-teal-700 hover:bg-teal-50"
                            data-testid={`download-${resource.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            {resource.file_type === "link" ? "Open" : "Download"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-700">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Need More Information?
          </h2>
          <p className="text-teal-100 mb-6">
            Can't find what you're looking for? Contact us and we'll help you find the resources you need.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-teal-700 hover:bg-teal-50 font-bold uppercase tracking-wide text-sm px-8">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
